import type { Abi, Address } from "viem";
import { config } from "../config";
import { erc20Abi } from "../abi";
import { ensureChain, l2Chain, l2Public, walletFor } from "./chain";

/** Send an L2 write with the injected wallet, ensuring the wallet is on the L2 first, and wait for
 *  the receipt. Returns the tx hash. */
export async function writeL2(
  account: Address,
  params: { address: Address; abi: Abi; functionName: string; args?: readonly unknown[] },
): Promise<string> {
  await ensureChain(config.l2);
  const wallet = walletFor(l2Chain, account);
  if (!wallet) throw new Error("No injected wallet available.");
  const hash = await wallet.writeContract({
    account,
    chain: l2Chain,
    address: params.address,
    abi: params.abi,
    functionName: params.functionName,
    args: params.args ?? [],
  });
  await l2Public.waitForTransactionReceipt({ hash });
  return hash;
}

/** Approve `spender` to pull `amount` of an ERC-20 if the current allowance is short. */
export async function approveIfNeeded(
  account: Address,
  token: Address,
  spender: Address,
  amount: bigint,
): Promise<void> {
  const allowance = (await l2Public.readContract({
    address: token,
    abi: erc20Abi,
    functionName: "allowance",
    args: [account, spender],
  })) as bigint;
  if (allowance >= amount) return;
  await writeL2(account, { address: token, abi: erc20Abi, functionName: "approve", args: [spender, amount] });
}
